import { useCallback } from "react";
import { v4 as uuid } from "uuid";
import {
  STORAGE_KEYS,
  PREVIEW_DATA_EXPIRY_MS,
} from "../constants";
import {
  StravaPreviewData,
  StravaExchangeResponse,
} from "../types";
import { PosterProject, GPSSource, GPSData } from "PosterEditor/model/poster-project";
import { defaultProject, defaultGPSSource } from "PosterEditor/model/default-project-style";
import { extractGPSData } from "PosterEditor/utils/gps-data";

/**
 * Hook for managing Strava preview data in localStorage.
 *
 * This allows users who come from Strava to preview their activity on a poster
 * before creating an account. The data persists for 6 hours (matches Strava access token lifetime).
 */
export const useStravaPreviewData = () => {
  /**
   * Save preview data to localStorage.
   *
   * @param data - The Strava preview data to save
   */
  const savePreviewData = useCallback((data: StravaPreviewData): void => {
    localStorage.setItem(STORAGE_KEYS.PREVIEW_DATA, JSON.stringify(data));
  }, []);

  /**
   * Load preview data from localStorage.
   * Returns null if not found or expired (>6 hours old).
   */
  const getPreviewData = useCallback((): StravaPreviewData | null => {
    const stored = localStorage.getItem(STORAGE_KEYS.PREVIEW_DATA);
    if (!stored) {
      return null;
    }

    try {
      const data: StravaPreviewData = JSON.parse(stored);

      // Check if data has expired
      if (Date.now() - data.fetchedAt > PREVIEW_DATA_EXPIRY_MS) {
        console.log("Strava preview data has expired, clearing");
        localStorage.removeItem(STORAGE_KEYS.PREVIEW_DATA);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Failed to parse Strava preview data from localStorage:", error);
      localStorage.removeItem(STORAGE_KEYS.PREVIEW_DATA);
      return null;
    }
  }, []);

  /**
   * Check if user has Strava preview data (indicating they're in the Strava flow).
   */
  const hasStravaData = useCallback((): boolean => {
    return getPreviewData() !== null;
  }, [getPreviewData]);

  /**
   * Clear preview data from localStorage.
   * Call this after successfully creating an account and migrating data.
   */
  const clearPreviewData = useCallback((): void => {
    localStorage.removeItem(STORAGE_KEYS.PREVIEW_DATA);
  }, []);

  /**
   * Convert Strava exchange response to preview data format.
   *
   * @param response - The response from the strava-exchange Edge Function
   * @returns The preview data, or null if no activity/coordinates available
   */
  const createPreviewDataFromResponse = useCallback(
    (response: StravaExchangeResponse): StravaPreviewData | null => {
      if (!response.activity || !response.coordinates || response.coordinates.length === 0) {
        console.warn("No activity or coordinates in Strava response");
        return null;
      }

      const { activity, coordinates, athlete } = response;

      // Calculate GPS data from coordinates
      const gpsData = extractGPSData({ allCoordinates: coordinates }) ?? {
        elevationGain: activity.totalElevationGain,
        distanceMeters: activity.distance,
        start: new Date(activity.startDate),
        end: undefined,
      };

      return {
        activityId: activity.id,
        activityName: activity.name,
        activityType: activity.type,
        startDate: activity.startDate,
        coordinates,
        gpsData,
        athleteFirstName: athlete.firstName,
        fetchedAt: Date.now(),
        stravaDistance: activity.distance,
        stravaElevationGain: activity.totalElevationGain,
        stravaMovingTime: activity.movingTime,
      };
    },
    []
  );

  /**
   * Convert preview data to a guest project format.
   * This creates a project that can be saved via the standard project store.
   *
   * @returns A PosterProject configured with the Strava activity data
   */
  const convertToGuestProject = useCallback((): PosterProject | null => {
    const previewData = getPreviewData();
    if (!previewData) {
      return null;
    }

    const gpsSourceId = uuid();

    // Create GPS source from preview data
    const gpsSource: GPSSource = {
      ...defaultGPSSource,
      id: gpsSourceId,
      name: previewData.activityName,
      originDescription: `Strava: ${previewData.activityType}`,
      allCoordinates: previewData.coordinates,
      displayedCoordinates: previewData.coordinates,
      data: previewData.gpsData,
    };

    // Create project with Strava data
    const project: PosterProject = {
      ...defaultProject,
      id: "guest_project",
      name: previewData.activityName,
      createdAt: new Date(),
      updatedAt: new Date(),
      isTemplateDraft: true,
      gpsSourceIDs: [gpsSourceId],
      gpsData: {
        id: gpsSourceId,
        data: previewData.gpsData,
      },
    };

    return project;
  }, [getPreviewData]);

  /**
   * Get the GPS source from preview data for use with the GPS store.
   *
   * @returns A GPSSource configured with the Strava activity data, or null
   */
  const getGPSSourceFromPreviewData = useCallback((): GPSSource | null => {
    const previewData = getPreviewData();
    if (!previewData) {
      return null;
    }

    return {
      ...defaultGPSSource,
      id: uuid(),
      name: previewData.activityName,
      originDescription: `Strava: ${previewData.activityType}`,
      allCoordinates: previewData.coordinates,
      displayedCoordinates: previewData.coordinates,
      data: previewData.gpsData,
    };
  }, [getPreviewData]);

  return {
    savePreviewData,
    getPreviewData,
    hasStravaData,
    clearPreviewData,
    createPreviewDataFromResponse,
    convertToGuestProject,
    getGPSSourceFromPreviewData,
  };
};
